import { useQuery } from '@apollo/client';
import { Autocomplete, TextField, type AutocompleteProps } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useAppContext } from '~/contexts';
import { AvailableShowsDocument } from './ShowAutocomplete.generated';

export interface ShowAutocompleteProps
  extends Omit<AutocompleteProps<number, false, false, false>, 'options' | 'renderInput'> {
  error?: boolean;
  helperText?: React.ReactNode;
  name?: string;
}

export const ShowAutocomplete = ({
  error,
  helperText,
  name,
  ...autocompleteProps
}: ShowAutocompleteProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data, loading } = useQuery(AvailableShowsDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const showsById = useMemo(() => {
    return new Map(data?.network?.allShows.map((show) => [show.id, show] as const) ?? []);
  }, [data?.network?.allShows]);

  const options = useMemo(() => [...showsById.keys()], [showsById]);

  if (loading && !data?.network?.allShows) return null;

  return (
    <Autocomplete<number>
      fullWidth
      getOptionLabel={(option) => showsById.get(option)?.name ?? ''}
      loading={loading}
      options={options}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          name={name}
          {...namedByPlaceholder(
            params,
            t('pages.device_signals.settings.placeholders.select_show'),
          )}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option}>
          {showsById.get(option)?.name}
        </li>
      )}
      {...autocompleteProps}
    />
  );
};
